import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faBriefcase, faArrowRight, faSearch,
  faCircle, faChartLine, faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FadeUp } from '../components/common/FadeUp';
import { GlassCard } from '../components/common/GlassCard';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { SkeletonCard } from '../components/common/Skeleton';
import { Modal } from '../components/common/Modal';
import { FormField } from '../components/common/FormField';
import { EmptyState } from '../components/common/EmptyState';
import { useToast } from '../hooks/useToast';
import { deals as dealsApi, templates as templatesApi } from '../api';
import type { Deal, DealStage, Template } from '../types';

const STAGE_CONFIG: Record<DealStage, { label: string; color: string }> = {
  discovery: { label: '탐색', color: 'var(--color-info)' },
  qualification: { label: '검증', color: 'var(--color-accent)' },
  proposal: { label: '제안', color: 'var(--color-warning)' },
  negotiation: { label: '협상', color: 'oklch(0.65 0.18 50)' },
  closed_won: { label: '성사', color: 'var(--color-success)' },
  closed_lost: { label: '실패', color: 'var(--color-error)' },
};

function formatCurrency(value: number, currency = 'KRW') {
  return new Intl.NumberFormat('ko-KR', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
}

export function Deals() {
  const [dealsList, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [templatesList, setTemplates] = useState<Template[]>([]);
  const [form, setForm] = useState({ name: '', company_name: '', value: '', template_id: '', stage: 'discovery', expected_close_date: '' });
  const [creating, setCreating] = useState(false);
  const { addToast } = useToast();

  const loadDeals = useCallback(async () => {
    try {
      const data = await dealsApi.list(stageFilter === 'all' ? undefined : stageFilter);
      setDeals(data);
    } catch { addToast('error', '딜 목록을 불러올 수 없습니다'); }
    finally { setLoading(false); }
  }, [stageFilter, addToast]);

  useEffect(() => { loadDeals(); }, [loadDeals]);
  useEffect(() => { templatesApi.list().then(setTemplates).catch(() => {}); }, []);

  const filtered = dealsList.filter(d =>
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    d.company_name.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      await dealsApi.create({
        name: form.name,
        company_name: form.company_name,
        value: Number(form.value) || 0,
        template_id: Number(form.template_id) || undefined,
        stage: form.stage as DealStage,
        expected_close_date: form.expected_close_date || undefined,
      } as Partial<Deal>);
      addToast('success', '딜이 생성되었습니다');
      setShowCreate(false);
      setForm({ name: '', company_name: '', value: '', template_id: '', stage: 'discovery', expected_close_date: '' });
      loadDeals();
    } catch (err) { addToast('error', err instanceof Error ? err.message : 'Failed'); }
    finally { setCreating(false); }
  };

  const totalValue = dealsList.filter(d => d.stage !== 'closed_lost').reduce((s, d) => s + d.value, 0);

  return (
    <main id="main-content" role="main" className="container" style={{ paddingTop: 'var(--space-20)', paddingBottom: 'var(--space-16)' }}>
      <FadeUp>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)', flexWrap: 'wrap', gap: 'var(--space-4)' }}>
          <div>
            <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--space-2)' }}>
              <FontAwesomeIcon icon={faBriefcase} style={{ color: 'var(--color-accent)', marginRight: 'var(--space-3)' }} />
              딜 관리
            </h1>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
              {dealsList.length} 딜 &middot; 파이프라인: {formatCurrency(totalValue)}
            </p>
          </div>
          <Button variant="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setShowCreate(true)}>
            새 딜
          </Button>
        </div>
      </FadeUp>

      {/* Search + Filter */}
      <FadeUp delay={0.05}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-6)', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 200px' }}>
            <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="딜 검색..."
              aria-label="딜 검색"
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-4) var(--space-2) var(--space-8)',
                fontSize: 'var(--text-sm)',
                fontFamily: 'var(--font-sans)',
                color: 'var(--color-text-primary)',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
            {['all', ...Object.keys(STAGE_CONFIG)].map(s => (
              <button
                key={s}
                onClick={() => { setStageFilter(s); setLoading(true); }}
                aria-pressed={stageFilter === s}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-full)',
                  border: '1px solid',
                  borderColor: stageFilter === s ? 'var(--color-accent)' : 'var(--color-border)',
                  background: stageFilter === s ? 'var(--color-accent-subtle)' : 'transparent',
                  color: stageFilter === s ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {s === 'all' ? '전체' : STAGE_CONFIG[s as DealStage].label}
              </button>
            ))}
          </div>
        </div>
      </FadeUp>

      {/* Deal Cards */}
      <div className="deals-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)' }}>
        {loading ? (
          Array.from({ length: 4 }, (_, i) => <FadeUp key={i} delay={i * 0.05}><SkeletonCard /></FadeUp>)
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={faBriefcase}
            title="아직 딜이 없습니다"
            description="첫 딜을 생성하여 파이프라인 관리를 시작하세요"
            action={<Button variant="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setShowCreate(true)}>딜 생성</Button>}
          />
        ) : filtered.map((deal, i) => {
          const cfg = STAGE_CONFIG[deal.stage];
          return (
            <FadeUp key={deal.id} delay={i * 0.03}>
              <Link to={`/deals/${deal.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <GlassCard as="article" style={{ cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                        <Badge color={cfg.color}>
                          <FontAwesomeIcon icon={faCircle} style={{ fontSize: '6px' }} /> {cfg.label}
                        </Badge>
                        {deal.company_name && (
                          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{deal.company_name}</span>
                        )}
                      </div>
                      <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.name}</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
                        <span><FontAwesomeIcon icon={faChartLine} style={{ marginRight: 'var(--space-1)' }} />{formatCurrency(deal.value, deal.currency)}</span>
                        {deal.owner_name && <span><FontAwesomeIcon icon={faUsers} style={{ marginRight: 'var(--space-1)' }} />{deal.owner_name}</span>}
                        {deal.expected_close_date && <span>클로징: {deal.expected_close_date}</span>}
                      </div>
                    </div>
                    <FontAwesomeIcon icon={faArrowRight} style={{ color: 'var(--color-text-tertiary)', flexShrink: 0, marginTop: 'var(--space-2)' }} />
                  </div>
                </GlassCard>
              </Link>
            </FadeUp>
          );
        })}
      </div>

      {/* Create Deal Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="새 딜 생성" footer={
        <>
          <Button variant="ghost" onClick={() => setShowCreate(false)}>취소</Button>
          <Button variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? '생성 중...' : '딜 생성'}
          </Button>
        </>
      }>
        <form onSubmit={handleCreate}>
          <FormField label="딜 이름" required value={form.name} onChange={e => setForm(p => ({ ...p, name: (e.target as HTMLInputElement).value }))} placeholder="예: Acme Corp Enterprise" />
          <FormField label="회사" value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: (e.target as HTMLInputElement).value }))} placeholder="회사명" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <FormField label="금액" type="number" value={form.value} onChange={e => setForm(p => ({ ...p, value: (e.target as HTMLInputElement).value }))} placeholder="0" />
            <FormField label="단계" as="select" value={form.stage} onChange={e => setForm(p => ({ ...p, stage: (e.target as HTMLSelectElement).value }))}>
              {Object.entries(STAGE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </FormField>
          </div>
          <FormField label="템플릿" as="select" value={form.template_id} onChange={e => setForm(p => ({ ...p, template_id: (e.target as HTMLSelectElement).value }))}>
            <option value="">없음</option>
            {templatesList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </FormField>
          <FormField label="예상 클로징일" type="date" value={form.expected_close_date} onChange={e => setForm(p => ({ ...p, expected_close_date: (e.target as HTMLInputElement).value }))} />
        </form>
      </Modal>

      <style>{`
        @media (min-width: 768px) { .deals-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>
    </main>
  );
}
